import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../redux/network/https";
import publicClient from "../publicClient";
import { endPoints, queryKeys } from "../../redux/network/constant";

// ── Shared Types ──────────────────────────────────────────────────────────────

export interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  items: T[];
  total_count: number;
  total_pages: number;
  page: number;
  page_size: number;
  count?: number;
  current_page?: number;
}

// ── User / Assignee ───────────────────────────────────────────────────────────

export interface TaskAssignee {
  id: number;
  email: string;
  name: string | null;
  profile_image_url: string | null;
}

export interface ProjectOwner {
  id: number;
  name: string | null;
  profile_image_url: string | null;
}

export interface ProjectMemberBrief {
  id: number;
  email: string;
  name: string | null;
  profile_image_url: string | null;
  role: "MANAGER" | "COLLABORATOR" | "EXTERNAL_STAKEHOLDER";
  status: "PENDING" | "ACTIVE" | "INACTIVE";
}

export interface ProjectMemberUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string | null;
  full_name: string | null;
  phone_no: string;
  profession: string | null;
  profile_image_url: string | null;
  skills: string[];
}

export interface ProjectMember {
  id: number;
  project: number;
  user: ProjectMemberUser;
  role: string;
  status: string;
  title: string;
  active_task_count: number;
  invitation_expires_at: string | null;
  cancelled_at: string | null;
  is_invitation_expired: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectInviteContext {
  project_id: number;
  project_title: string;
  project_description: string;
  project_status: string;
  inviter_name: string;
  inviter_profile_image_url?: string | null;
  team_name?: string | null;
  role: string;
  expires_at: string | null;
  is_client_invite: boolean;
  member_id: number;
}

// ── Project ───────────────────────────────────────────────────────────────────

export type ProjectStatus = "ACTIVE" | "PENDING" | "DONE";

export interface Project {
  id: number;
  title: string;
  description: string;
  status: ProjectStatus;
  start_date: string | null;
  end_date: string | null;
  completion_percentage: number;
  owner: ProjectOwner;
  members: ProjectMemberBrief[];
  created_at: string;
  updated_at: string;
  header_gradient: string | null;
  user_membership_status: string | null;
  user_role: string | null;
  member_id: number | null;
  invitation_expires_at: string | null;
  team_name: string | null;
  team_is_personal: boolean | null;
  client_id: number | null;
  client?: {
    id: number;
    company_name: string;
    contact_name: string;
    industry: string;
  } | null;
}

// ── Task ──────────────────────────────────────────────────────────────────────

export type TaskStatus = "PENDING" | "ACTIVE" | "DONE";
export type TaskPriority = "LOW" | "MEDIUM" | "HIGH";

export interface Task {
  id: number;
  project: number;
  created_by: number;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  start_date: string | null;
  due_date: string | null;
  color: string | null;
  sort_index: number;
  tags: string[];
  blocked_by: number[];
  assignees: TaskAssignee[];
  created_at: string;
  updated_at: string;
}

export interface GanttTask extends Task {
  project_title: string;
}

// ── Dashboard Types ──────────────────────────────────────────────────────────

export interface DashboardStatusCounts {
  total: number;
  active: number;
  pending: number;
  done: number;
}

export interface DashboardStats {
  projects: DashboardStatusCounts;
  tasks: DashboardStatusCounts;
  pending_invitations: number;
  pending_connections: number;
  clients: { total: number };
  files: { total: number };
}

export interface TaskTrendEntry {
  date: string;
  created: number;
  completed: number;
}

export interface UpcomingTask {
  id: number;
  title: string;
  due_date: string | null;
  priority: TaskPriority;
  status: TaskStatus;
  project_id: number;
  project_title: string;
}

// ── Filter / Param Types ──────────────────────────────────────────────────────

export interface ProjectListFilters {
  search?: string;
  ordering?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
  team_id?: number | null;
  include_all?: boolean;
  page?: number;
  page_size?: number;
}

export interface ProjectMemberFilters {
  search?: string;
  role?: string;
  status?: string;
}

// ── Mutation Payloads ─────────────────────────────────────────────────────────

export interface CreateProjectPayload {
  title: string;
  description: string;
  status?: ProjectStatus;
  start_date?: string;
  end_date?: string;
  header_gradient?: string;
}

export interface InviteMemberPayload {
  user_id?: number;
  email?: string;
  role: string;
  title?: string;
  contact_name?: string;
  company_name?: string;
  phone?: string;
  address?: string;
  industry?: string;
}

export interface UpdateMemberPayload {
  role?: string;
  title?: string;
}

export interface TransferProjectPayload {
  projectId: number;
  team_id: number;
  fromTeamId: number | null;
}

// ── Projects List ─────────────────────────────────────────────────────────────

const fetchProjectsList = async ({ queryKey }: { queryKey: readonly unknown[] }) => {
  const [, , filters] = queryKey as [string, string, ProjectListFilters | undefined];

  const {
    search,
    ordering = "-updated_at",
    start_date,
    end_date,
    status,
    team_id,
    include_all,
    page = 1,
    page_size = 10,
  } = filters || {};

  const params = new URLSearchParams();
  if (search && typeof search === "string" && search.trim().length > 0) {
    params.append("search", search.trim());
  }
  if (ordering) params.append("ordering", ordering);
  if (start_date && end_date) {
    params.append("start_date", start_date);
    params.append("end_date", end_date);
  }
  if (status) params.append("status", status);
  if (team_id) params.append("team_id", String(team_id));
  if (include_all) params.append("include_all", "true");
  if (page) params.append("page", String(page));
  if (page_size) params.append("page_size", String(page_size));

  let url = endPoints.ProjectsList;
  const qs = params.toString();
  if (qs) url += `?${qs}`;

  const { data } = await axiosInstance.get<ApiResponse<PaginatedResponse<Project>>>(url);
  return data?.data;
};

export const useProjectsList = (filters: ProjectListFilters = {}, enabled = true) => {
  const filtersKey: Required<ProjectListFilters> = {
    search: filters?.search || "",
    ordering: filters?.ordering || "-updated_at",
    start_date: filters?.start_date || "",
    end_date: filters?.end_date || "",
    status: filters?.status || "",
    team_id: filters?.team_id ?? null,
    include_all: filters?.include_all || false,
    page: filters?.page || 1,
    page_size: filters?.page_size || 10,
  };

  return useQuery({
    queryKey: queryKeys.ProjectList(filtersKey),
    queryFn: fetchProjectsList,
    enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

// ── Create Project ────────────────────────────────────────────────────────────

const postCreateProject = async (payload: CreateProjectPayload) => {
  const { data } = await axiosInstance.post<ApiResponse<Project>>(endPoints.CreateProject, payload);
  return data?.data;
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postCreateProject,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProjectListBase(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProfileDetails(),
      });
    },
  });
};

// ── Project Member Invites ────────────────────────────────────────────────────

const postInviteMember = async ({ projectId, payload }: { projectId: number; payload: InviteMemberPayload }) => {
  const { data } = await axiosInstance.post<ApiResponse<ProjectMember>>(
    endPoints.ProjectMembersAdd(projectId),
    payload
  );
  return data?.data;
};

export const useInviteMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postInviteMember,
    onSuccess: (_data, variables) => {
      if (variables?.projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.ProjectDetail(variables.projectId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.ProjectMembers(variables.projectId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProfileDetails(),
      });
    },
  });
};

const postRemoveMember = async ({ projectId, userId }: { projectId: number; userId: number }) => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(
    endPoints.ProjectMemberRemove(projectId, userId)
  );
  return data?.data;
};

export const useRemoveMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postRemoveMember,
    onSuccess: (_data, variables) => {
      if (variables?.projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.ProjectMembers(variables.projectId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.ProjectDetail(variables.projectId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProfileDetails(),
      });
    },
  });
};

const patchUpdateProjectMember = async ({ projectId, userId, data: payload }: { projectId: number; userId: number; data: UpdateMemberPayload }) => {
  const { data: responseData } = await axiosInstance.patch<ApiResponse<ProjectMember>>(
    endPoints.ProjectMemberRoleUpdate(projectId, userId),
    payload
  );
  return responseData?.data;
};

export const useUpdateProjectMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchUpdateProjectMember,
    onSuccess: (_data, variables) => {
      if (variables?.projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.ProjectMembers(variables.projectId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.ProjectDetail(variables.projectId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.ProfileDetails(),
      });
    },
  });
};

// ── Project Members List ──────────────────────────────────────────────────────

const fetchProjectMembers = async ({ queryKey }: { queryKey: readonly unknown[] }) => {
  const [, , projectId, filters] = queryKey as [string, string, number, ProjectMemberFilters | undefined];
  const params = new URLSearchParams();
  if (filters?.search) params.set('search', filters.search);
  if (filters?.role) params.set('role', filters.role);
  if (filters?.status) params.set('status', filters.status);
  const qs = params.toString();
  const url = qs ? `${endPoints.ProjectMembersList(projectId)}?${qs}` : endPoints.ProjectMembersList(projectId);
  const { data } = await axiosInstance.get<ApiResponse<ProjectMember[]>>(url);
  return data?.data;
};

export const useProjectMembers = (projectId: number | null, filters: ProjectMemberFilters = {}, enabled = true) => {
  const filtersKey: Required<ProjectMemberFilters> = { search: filters?.search || '', role: filters?.role || '', status: filters?.status || '' };
  return useQuery({
    queryKey: queryKeys.ProjectMembers(projectId, filtersKey),
    queryFn: fetchProjectMembers,
    enabled: !!projectId && enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

// ── Accept Project Invite ─────────────────────────────────────────────────────

export interface AcceptedInviteClient {
  id: number;
  project_count?: number;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string;
  address: string;
  industry: string;
}

export interface AcceptInviteResponse {
  client: AcceptedInviteClient | null;
}

const postAcceptInvite = async ({ token }: { token: string }) => {
  const { data } = await axiosInstance.post<ApiResponse<AcceptInviteResponse>>(
    endPoints.ProjectInviteAccept(token)
  );
  return data;
};

const fetchProjectInviteContext = async ({ queryKey }: { queryKey: readonly unknown[] }) => {
  const [, , , token] = queryKey as [string, string, string, string];
  const { data } = await publicClient.get<ApiResponse<ProjectInviteContext>>(
    endPoints.ProjectInviteDetail(token),
  );
  return data?.data;
};

export const useGetProjectInviteContext = (token: string | null, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.ProjectInviteDetail(token),
    queryFn: fetchProjectInviteContext,
    enabled: !!token && enabled,
    retry: false,
    staleTime: 0,
  });
};

export const useAcceptProjectInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postAcceptInvite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ProjectListBase() });
      queryClient.invalidateQueries({ queryKey: queryKeys.ProfileDetails() });
    },
  });
};

// ── Dashboard Invite ──────────────────────────────────────────────────────────

const postAcceptDashboardInvite = async ({ memberId }: { memberId: number }) => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(
    endPoints.ProjectInviteDashboardAccept(memberId),
  );
  return data;
};

export const useAcceptDashboardInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postAcceptDashboardInvite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ProjectListBase() });
      queryClient.invalidateQueries({ queryKey: queryKeys.ProfileDetails() });
    },
  });
};

const postRejectDashboardInvite = async ({ memberId }: { memberId: number }) => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(
    endPoints.ProjectInviteDashboardReject(memberId),
  );
  return data;
};

export const useRejectDashboardInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postRejectDashboardInvite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ProjectListBase() });
      queryClient.invalidateQueries({ queryKey: queryKeys.ProfileDetails() });
    },
  });
};

const postCancelDashboardInvite = async ({
  memberId,
  projectId,
}: {
  memberId: number;
  projectId: number;
}) => {
  const { data } = await axiosInstance.post<ApiResponse<ProjectMember>>(
    endPoints.ProjectInviteDashboardCancel(memberId),
  );
  return data;
};

export const useCancelDashboardInvite = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postCancelDashboardInvite,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ProjectListBase() });
      queryClient.invalidateQueries({ queryKey: queryKeys.ProfileDetails() });
      if (variables?.projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.ProjectDetail(variables.projectId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.ProjectMembers(variables.projectId),
        });
      }
    },
  });
};

// ── Delete Project ────────────────────────────────────────────────────────────

const deleteProjectById = async ({ projectId }: { projectId: number }) => {
  const { data } = await axiosInstance.delete<ApiResponse<null>>(
    endPoints.ProjectDetails(projectId),
  );
  return data;
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteProjectById,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ProjectListBase() });
      queryClient.invalidateQueries({ queryKey: queryKeys.ProfileDetails() });
      if (variables?.projectId) {
        queryClient.removeQueries({
          queryKey: queryKeys.ProjectDetail(variables.projectId),
        });
        queryClient.removeQueries({
          queryKey: queryKeys.ProjectMembers(variables.projectId),
        });
        queryClient.removeQueries({
          queryKey: queryKeys.ProjectTasksBase(variables.projectId),
        });
      }
    },
  });
};

// ── Transfer Project ──────────────────────────────────────────────────────────

const postTransferProject = async ({
  projectId,
  team_id,
}: TransferProjectPayload) => {
  const { data } = await axiosInstance.post<ApiResponse<Project>>(
    endPoints.ProjectTransfer(projectId),
    { team_id }
  );
  return data?.data;
};

export const useTransferProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postTransferProject,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ProjectListBase() });
      queryClient.invalidateQueries({ queryKey: queryKeys.DashboardStats() });
      if (variables?.fromTeamId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.TeamDetail(variables.fromTeamId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.TeamStorage(variables.fromTeamId),
        });
      }
      if (variables?.team_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.TeamDetail(variables.team_id),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.TeamStorage(variables.team_id),
        });
      }
      if (variables?.projectId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.ProjectDetail(variables.projectId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.ProjectFilesUsage(variables.projectId),
        });
      }
    },
  });
};

// ── Dashboard Stats ───────────────────────────────────────────────────────────

const fetchDashboardStats = async (): Promise<DashboardStats> => {
  const { data } = await axiosInstance.get<ApiResponse<DashboardStats>>(
    endPoints.DashboardStats,
  );
  return data?.data;
};

export const useDashboardStats = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.DashboardStats(),
    queryFn: fetchDashboardStats,
    enabled,
    staleTime: 60 * 1000,
  });
};

// ── Task Trend ────────────────────────────────────────────────────────────────

const fetchTaskTrend = async (days: number): Promise<{ trend: TaskTrendEntry[] }> => {
  const { data } = await axiosInstance.get<ApiResponse<{ trend: TaskTrendEntry[] }>>(
    endPoints.TaskTrend,
    { params: { days } },
  );
  return data?.data;
};

export const useTaskTrend = (days = 30, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.TaskTrend(days),
    queryFn: () => fetchTaskTrend(days),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

// ── Upcoming Tasks ────────────────────────────────────────────────────────────

const fetchUpcomingTasks = async (days: number, limit: number): Promise<{ tasks: UpcomingTask[] }> => {
  const { data } = await axiosInstance.get<ApiResponse<{ tasks: UpcomingTask[] }>>(
    endPoints.UpcomingTasks,
    { params: { days, limit } },
  );
  return data?.data;
};

export const useUpcomingTasks = (days = 30, limit = 10, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.UpcomingTasks(days, limit),
    queryFn: () => fetchUpcomingTasks(days, limit),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};
